import { AsyncPipe } from '@angular/common';
import { Component, inject, input, output } from '@angular/core';
import { Organization } from '../../../organization-models';
import { ModuleService } from '../../../../../shared/services/module.service';
import { ModuleMatrixSearchEvent, ModuleModel } from '../../../../modules/module-model';
import { toObservable } from '@angular/core/rxjs-interop';
import { catchError, combineLatest, filter, map, of, shareReplay, startWith, Subject, switchMap, take } from 'rxjs';
import { SnackbarService } from 'ok-angular-shared/components';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'adm-step5-modules',
  templateUrl: './step5-modules.component.html',
  styleUrl: './step5-modules.component.scss',
  imports: [MatSlideToggleModule, MatButtonModule, AsyncPipe],
})
export class Step5ModulesComponent {
  private moduleService = inject(ModuleService);
  private snackbarService = inject(SnackbarService);
  private reloadOrganizationModulesTrigger$ = new Subject<void>();

  organization = input.required<Organization>();

  skipStep = output<void>();

  private organization$ = toObservable(this.organization).pipe(filter(organization => organization?.id !== null));

  public modules$ = this.moduleService.getModules().pipe(
    catchError(() => {
      this.snackbarService.error('Der opstod en ukendt fejl ved hentning af moduler.');
      return of([] as ModuleModel[]);
    }),
    shareReplay(1)
  );

  public organizationModules$ = combineLatest([this.organization$, this.reloadOrganizationModulesTrigger$.pipe(startWith(undefined))]).pipe(
    switchMap(([organization]) => {
      const searchEvent = new ModuleMatrixSearchEvent();
      searchEvent.organizationId = organization.id;
      return this.moduleService.getModuleMatrix(searchEvent);
    }),
    map(matrix => {
      if (!matrix.items[0]) {
        this.snackbarService.error('Der opstod en ukendt fejl ved hentning af moduler for organisationen.');
        return null;
      }
      return matrix.items[0];
    }),
    catchError(() => {
      this.snackbarService.error('Der opstod en ukendt fejl ved hentning af moduler for organisationen.');
      return of(null);
    }),
    shareReplay(1)
  );

  toggleModuleForOrganization(moduleId: number, currentValue: boolean) {
    const organizationId = this.organization().id;
    const toggleRequest$ = currentValue
      ? this.moduleService.deleteModuleMatrix(organizationId, moduleId)
      : this.moduleService.createModuleMatrix(organizationId, moduleId);

    toggleRequest$.pipe(take(1)).subscribe({
      next: () => {
        this.reloadOrganizationModulesTrigger$.next();
      },
      error: () => {
        this.snackbarService.error('Der opstod en ukendt fejl under tilknytning af moduler.');
      },
    });
  }
}
